import { zodResolver } from "@hookform/resolvers/zod";
import {
  KikCustomerFlags as CustomerFlags,
  kikCustomerFlagsSchema as customerFlagsSchema,
  KikCustomerPersonalInfoFields as CustomerPersonalInfoFields,
  kikCustomerPersonalInfoSchema as customerPersonalInfoSchema,
  KikUserZodSchemas as ZodSchemas,
} from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { hooks } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import dayjs, { Dayjs } from "dayjs";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { omit, pick } from "ramda";
import { useEffect, useRef } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import Icon from "~/components/common/icon";
import myaccount from "~/components/common/myaccount.module.scss";
import Checkbox from "~/components/form/checkbox";
import ControlledPhoneInput from "~/components/form/controlled-phone-input";
import Errors from "~/components/form/errors";
import InputFieldDateMui from "~/components/form/input-field-date-mui";
import SelectGeneric from "~/components/form/select-generic";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import useSubscriptions from "~/hooks/use-subscriptions";
import Logger from "~/services/logger/logger";
import CommonUtils from "~/utils/common-utils";
import crossCountryUtils from "~/utils/cross-country-utils";

import Button from "../../common/button";
import InputField from "../../form/input-field";
import styles from "./settings.module.scss";

type Props = {
  user: EVA.Core.LoggedInUserDto;
  onSubmitSuccess: () => void;
};

export default function PersonalInfoForm(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const { updateUserProfile, authError, isLoading, refreshUserData } = useAuthContext();
  const {
    hasLoyaltyProgram,
    pushSubscriptionsByFlags,
    removeSubscriptionsByFlags,
    userSubscriptions,
    userSubscriptionsToFlags,
  } = useSubscriptions();

  const updateUser = hooks.useCallService({ service: Core.UpdateUser });

  const availableLanguages = crossCountryUtils.getAvailableUserLanguages(router);
  // const disabledLanguageSelect = availableLanguages.length < 2;
  const localizedCountryName = crossCountryUtils.getLocalizedCountryName(props.user.CountryID, router);

  const {
    control,
    register,
    handleSubmit,
    clearErrors,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<CustomerPersonalInfoFields>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
    resolver: zodResolver(customerPersonalInfoSchema),
    defaultValues: {
      ...props.user,
    },
  });

  const onSubmit: SubmitHandler<CustomerPersonalInfoFields> = async (data) => {
    try {
      const flagKeys: (keyof CustomerFlags)[] = Object.keys(customerFlagsSchema.shape) as (keyof CustomerFlags)[];
      // Get the modified flags only => Manage the subscriptions later
      const flags = pick(
        flagKeys.filter((k) => {
          return data[k as keyof CustomerFlags] !== optInCheckboxes[k as keyof CustomerFlags];
        }),
        data
      );
      // Exclude the flags (get all the rest) => Manage the update user
      const userData = omit(flagKeys, data);

      if (props.user?.ID) {
        await updateUser({
          ID: props.user.ID,
          ...userData,
        });
        await updateUserProfile(props.user.FirstName, props.user.LastName);
        refreshUserData();
        await pushSubscriptionsByFlags(flags);
        await removeSubscriptionsByFlags(flags);
        props.onSubmitSuccess();
      }
    } catch (err: any) {
      Logger.instance.warn("Error caught", err);
    }
  };

  const refOptInCheckboxes = useRef<Record<keyof CustomerFlags, boolean | void>>();
  const optInCheckboxes = userSubscriptionsToFlags();

  // Sync checkboxes appearance (checked/unchecked) with the real value of the associated privacy field
  useEffect(() => {
    if (
      !isLoading &&
      optInCheckboxes &&
      refOptInCheckboxes &&
      !CommonUtils.isEqual(optInCheckboxes, refOptInCheckboxes.current)
    ) {
      refOptInCheckboxes.current = optInCheckboxes;
      Object.entries(optInCheckboxes).forEach(([k, v]) => {
        setValue(k as keyof CustomerFlags, !!v);
      });
    }
  }, [optInCheckboxes, isLoading, refOptInCheckboxes, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.formGroup}>
        <h3 className={myaccount.titleXs}>{t("generic.myaccount.settings.personal_data")}</h3>
        <InputField
          {...register("FirstName")}
          type="text"
          label={t("generic.form.FirstName")}
          error={
            errors.FirstName?.message
              ? t(errors.FirstName?.message, {
                  fieldName: t("generic.form.FirstName"),
                  maxLength: ZodSchemas.firstNameMaxlength,
                })
              : ""
          }
          onClearErrors={() => clearErrors("FirstName")}
          maxLength={ZodSchemas.firstNameMaxlength}
        />
        <InputField
          {...register("LastName")}
          type="text"
          label={t("generic.form.LastName")}
          error={
            errors.LastName?.message
              ? t(errors.LastName?.message, {
                  fieldName: t("generic.form.LastName"),
                  maxLength: ZodSchemas.lastNameMaxlength,
                })
              : ""
          }
          onClearErrors={() => clearErrors("LastName")}
          maxLength={ZodSchemas.lastNameMaxlength}
        />
        <InputFieldDateMui
          control={control}
          name="DateOfBirth"
          label={t("generic.form.birthday")}
          error={
            errors.DateOfBirth?.message
              ? t(errors.DateOfBirth?.message, {
                  fieldName: t("generic.form.birthday"),
                  minimumAgeInYears: ZodSchemas.userMinimumAgeInYears,
                  maximumAgeInYears: ZodSchemas.userMaximumAgeInYears,
                })
              : ""
          }
          onClearErrors={() => clearErrors("DateOfBirth")}
          minimumDate={ZodSchemas.userBirthdayMinDto}
          maximumDate={ZodSchemas.userBirthdayMaxDto.minus({ day: 1 })}
        />
        <ControlledPhoneInput
          name="PhoneNumber"
          control={control}
          label={t("generic.form.PhoneNumber")}
          placeholder={t("generic.form.PhoneNumber_placeholder")}
          maxLength={ZodSchemas.phoneNumberMaxlength}
          isOptional
          error={
            errors.PhoneNumber?.message
              ? t(errors.PhoneNumber?.message, {
                  fieldName: t("generic.form.PhoneNumber"),
                  maxLength: ZodSchemas.phoneNumberMaxlength,
                  minLength: ZodSchemas.phoneNumberMinlength,
                })
              : ""
          }
          onClearErrors={() => clearErrors("PhoneNumber")}
        />
      </div>
      <div className={styles.formGroup}>
        <h3 className={myaccount.titleXs}>{t("generic.myaccount.settings.country_language")}</h3>
        <InputField value={localizedCountryName} disabled type="text" label={t("generic.form.CountryID")} />
        <SelectGeneric
          {...register("LanguageID")}
          // disabled={disabledLanguageSelect}
          label={t("generic.form.language")}
          options={availableLanguages}
          error={errors.LanguageID?.message}
        />
      </div>
      <div className={styles.formGroup} style={{ display: "flex", flexDirection: "column", border: "none" }}>
        <h3 className={myaccount.titleXs}>{t("generic.myaccount.settings.personal_data")}</h3>
        {Array.isArray(userSubscriptions) ? (
          <>
            {/* As per temporary agreement undertaken with Antonino we will display the field as disabled once users have subscribed to the loyalty program */}
            {hasLoyaltyProgram && (
              <div className={styles.loyaltyBox}>
                <Controller
                  name={"LoyaltyProgram"}
                  render={({ field }) => {
                    return (
                      <Checkbox
                        {...register("LoyaltyProgram")}
                        disabled={isLoading || !!optInCheckboxes?.LoyaltyProgram}
                        checked={field.value || false}
                        className={styles.privacyCheckbox}
                        label={t.rich("generic.signup_loyalty_program", {
                          br: () => <br />,
                          loyaltyParagraph: (chunks) => <p>{chunks}</p>,
                          strong: (chunks) => <strong>{chunks}</strong>,
                          benefitList: (chunks) => <ul className={styles.benefitList}>{chunks}</ul>,
                          benefit: (chunks) => <li className={styles.benefitItem}>{chunks}</li>,
                          benefitLabel: (chunks) => <span className={styles.benefitLabel}>{chunks}</span>,
                          svgIconServices: () => <Icon name="help" className={styles.benefitIcon} />,
                          svgIconBirthdayGift: () => <Icon name="birthday" className={styles.benefitIcon} />,
                          svgIconWelcomeGift: () => <Icon name="gift" className={styles.benefitIcon} />,
                          svgIconSpecialGifts: () => <Icon name="best-clients" className={styles.benefitIcon} />,
                          svgIconVIPCare: () => <Icon name="diamond" className={styles.benefitIcon} />,
                        })}
                        error={errors.LoyaltyProgram?.message}
                      />
                    );
                  }}
                  control={control}
                />
                <p className={styles.profilingPurposesDisclaimer}>
                  {t.rich("generic.profiling_purposes_with_link", {
                    underline: (chunks) => <span className={styles.underline}>{chunks?.toString()}</span>,
                  })}
                </p>
                <Controller
                  name={"ProfilingOptIn"}
                  render={({ field }) => {
                    return (
                      <Checkbox
                        {...register("ProfilingOptIn")}
                        disabled={isLoading}
                        checked={field.value || false}
                        className={styles.privacyCheckbox}
                        label={t("generic.online_profiling_opt_in")}
                        error={errors.ProfilingOptIn?.message}
                      />
                    );
                  }}
                  control={control}
                />
              </div>
            )}
            <Controller
              name={"MarketingOptIn"}
              render={({ field }) => {
                return (
                  <Checkbox
                    {...register("MarketingOptIn")}
                    disabled={isLoading}
                    checked={field.value || false}
                    className={styles.privacyCheckbox}
                    label={t("generic.marketing_opt_in")}
                    error={errors.MarketingOptIn?.message}
                  />
                );
              }}
              control={control}
            />
            <Controller
              name={"ThirdPartiesDataTranAuthOptIn"}
              render={({ field }) => {
                return (
                  <Checkbox
                    {...register("ThirdPartiesDataTranAuthOptIn")}
                    disabled={isLoading}
                    checked={field.value || false}
                    className={styles.privacyCheckbox}
                    label={t("generic.third_parties_data_tran_auth_opt_in")}
                    error={errors.ThirdPartiesDataTranAuthOptIn?.message}
                  />
                );
              }}
              control={control}
            />
          </>
        ) : (
          <></>
        )}
      </div>

      <Errors error={authError} style={{ gridColumn: breakpoint === "desktop" ? "2 span" : "1" }} />

      <Button
        className={styles.settingsCta}
        disabled={isSubmitting}
        variant="secondary"
        type="submit"
        loading={isLoading}
      >
        {t("generic.myaccount.settings.save_cta")}
      </Button>
    </form>
  );
}
